import { useState } from "react";
import { useHardNavigate } from "@/hooks/useHardNavigate";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Star } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { products, getProductBySlug } from "@/data/products";
import WishlistButton from "@/components/WishlistButton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProductCardProps {
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating: number;
  reviewCount: number;
  badge?: string;
  productId?: string;
}

const ProductCard = ({ name, price, originalPrice, image, rating, reviewCount, badge, productId }: ProductCardProps) => {
  const { addToCart, setIsCartOpen } = useCart();
  const [quantity, setQuantity] = useState(1);
  const navigate = useHardNavigate();
  
  // Find product by ID or name to get slug
  const product = productId 
    ? products.find(p => p.id === productId) 
    : products.find(p => p.name === name);
  
  const resolvedProductId = product?.id || "";
  const productSlug = product?.slug || "";
  
  // Handle card click - navigate only if not clicking on interactive elements
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Check if click is on interactive zone (quantity selector or cart button)
    if (target.closest('[data-interactive="true"]')) {
      return; // Don't navigate
    }
    if (productSlug) {
      navigate(`/product/${productSlug}`);
    }
  };
  
  // Unified: show toast notification on both desktop and mobile
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product) {
      addToCart(product, quantity, false);
      const qtyText = quantity > 1 ? `${quantity}x ` : "";
      toast.success(`${qtyText}${product.name} toegevoegd`, {
        action: {
          label: "Bekijk winkelwagen",
          onClick: () => setIsCartOpen(true),
        },
      });
      setQuantity(1); // Reset na toevoegen
    }
  };

  return (
    <div 
      className="group card-premium p-4 transition-all duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative aspect-square mb-4 overflow-hidden rounded-lg bg-secondary">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Badge */}
        {badge && (
          <div className="absolute top-3 left-3 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
            {badge}
          </div>
        )}

        {/* Wishlist Button - altijd zichtbaar op mobiel, hover op desktop */}
        {resolvedProductId && (
          <div 
            className="absolute top-3 right-3 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300"
            data-interactive="true"
            onClick={(e) => e.stopPropagation()}
          >
            <WishlistButton productId={resolvedProductId} size="sm" />
          </div>
        )}

      </div>

      {/* Content */}
      <div className="space-y-2">
        {/* Rating */}
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${i < rating ? "text-primary fill-primary" : "text-muted-foreground"}`}
            />
          ))}
          <span className="text-xs text-muted-foreground ml-1">({reviewCount})</span>
        </div>

        {/* Name */}
        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
          {name}
        </h3>

        {/* Price and Add to Cart Button */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-primary">€{price.toFixed(2)}</span>
            {originalPrice && (
              <span className="text-sm text-muted-foreground line-through">€{originalPrice.toFixed(2)}</span>
            )}
          </div>

          <div 
            className="flex items-center gap-1"
            data-interactive="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Quantity Selector */}
            <Select
              value={quantity.toString()} 
              onValueChange={(v) => setQuantity(parseInt(v))}
            >
              <SelectTrigger className="w-14 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <SelectItem key={n} value={n.toString()}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Add to Cart Button */}
            <Button 
              variant="outline" 
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={handleAddToCart}
              aria-label="Toevoegen aan winkelwagen"
            >
              <ShoppingCart className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
